/**
 * Contenido narrativo de las 3 ZONAS DE ACTIVIDAD (canon Adri 2026-07-11,
 * ver `docs/biblia/escenarios.md`):
 *   - LA CASA (el terreno de la caseta, NO el interior — eso vive en
 *     `src/data/interior.ts`)
 *   - EL CAMPO DE FÚTBOL CASA
 *   - EL MONTE
 *
 * Separación dura motor/contenido (mismo criterio que `interior.ts` y
 * `misiones.ts`): las escenas `LaCasaScene`, `CampoFutbolScene` y
 * `MonteScene` solo renderizan lo que hay aquí. Cero texto narrativo en
 * la escena.
 *
 * Convenciones:
 * - Tono callejero de colegas, humor GTA / Busted, sin nombres reales —
 *   solo apodos. Las menciones a vecinos son genéricas ("un vecino", "un
 *   viejo") o apodos canon de la biblia.
 * - Las "casas prohibidas" del monte (VENTURA y BAYONGA) son canon: si el
 *   jugador insiste en acercarse, un vecino llama a la policía y se lleva
 *   el 20% del dinero como multa. El porcentaje vive en `MULTA_PORCENTAJE`
 *   para que la escena lo aplique sin tener que hardcodearlo.
 * - La CASA RANDOM es donde arranca la Misión 1 ("La casa random"). La
 *   escena `MonteScene` consulta `GameState.misionCompletada(...)` y
 *   muestra un texto u otro según el estado.
 */

// -------------------------------------------------------------------------
// Porcentaje de multa por meterse en casa prohibida (canon: 20% del dinero)
// -------------------------------------------------------------------------

/** Porcentaje (0-1) del dinero que se lleva la policía al detener al
 *  jugador en una casa prohibida. Canon de Adri (ver escenarios.md). */
export const MULTA_PORCENTAJE = 0.2;

// -------------------------------------------------------------------------
// Descripciones cortas de ambiente (panel central de cada zona)
// -------------------------------------------------------------------------

/** Descripción ambient del terreno de LA CASA. */
export const DESCRIPCION_LA_CASA = {
  emoji: '🏡',
  nombre: 'LA CASA',
  ambient:
    'La caseta en el claro del monte. Sol cayendo a plomo, la barra de dominadas ' +
    'y la canasta ahí al lado. Terreno amplio de tierra y hierba seca.',
} as const;

/** Descripción ambient del CAMPO DE FÚTBOL CASA. */
export const DESCRIPCION_CAMPO_FUTBOL = {
  emoji: '⚽',
  nombre: 'EL CAMPO DE FÚTBOL CASA',
  ambient:
    'La pista de tierra pisada entre los árboles. El tronco de red sigue ' +
    'tumbado de lado a lado. Tablero con aro atado a un pino y un columpio ' +
    'colgando de una rama.',
} as const;

/** Descripción ambient del MONTE. */
export const DESCRIPCION_MONTE = {
  emoji: '🌲',
  nombre: 'EL MONTE',
  ambient:
    'Camino de tierra entre pinos y matorral. Casas dispersas, cada una con su ' +
    'verja. Aquí cada casa tiene sus normas, ya lo sabes.',
} as const;

// -------------------------------------------------------------------------
// Casas del MONTE: textos del aviso PROHIBIDO, la detención y la random
// -------------------------------------------------------------------------

/** Identificadores estables de las 3 casas del monte. La escena los usa
 *  para indexar el catálogo. */
export type CasaMonteId = 'ventura' | 'bayonga' | 'random';

/** Etiqueta visible de cada casa del monte (la pintada en la fachada). */
export const ETIQUETAS_CASAS_MONTE: Record<CasaMonteId, string> = {
  ventura: 'CASA DEL VENTURA',
  bayonga: 'CASA DEL BAYONGA',
  random: 'CASA RANDOM',
};

/** Aviso PROHIBIDO — se muestra al tocar una casa prohibida (canon: tono
 *  callejero con humor, sin nombres reales, solo apodos). */
export const AVISO_PROHIBIDO = {
  emoji: '⛔',
  titulo: 'PROHIBIDO — NI SE TE OCURRA',
  texto:
    'Esta casa es del VENTURA y aquí las normas las pone él. Si te ven, ' +
    'llaman a la policía y te caes del guindo. ¿Seguro que quieres acercarte?',
} as const;

/** Etiqueta del botón "insistir" del aviso prohibido (pasa al panel de
 *  detención). El escape siempre es el botón "Volver". */
export const BOTON_INSISTIR = 'Acercarse igualmente…';
export const BOTON_VOLVER_AVISO = 'Sí, mejor dar media vuelta';

/** Panel de DETENCIÓN — se muestra si el jugador insiste. Pierde el 20%
 *  del dinero como multa y vuelve al panel de zona. Tono GTA / "busted",
 *  humor callejero, solo apodos. */
export const DETENCION = {
  emoji: '🚔',
  titulo: 'BUSTED — Te han pillado',
  texto:
    'Un vecino te ha visto desde la verja, ha llamado a la poli y has acabado ' +
    'en el calabozo explicando que "solo estabas mirando". Te han soltado, pero ' +
    'la multa se la quedan igual. El VENTURA te ha visto la cara. No vuelvas.',
  /** Texto dinámico: la escena sustituye {multa} por el número calculado. */
  multaLinea: 'Multa: -{multa} € (20% de lo que llevabas encima)',
} as const;

/** Etiqueta del botón "OK" del panel de detención: vuelve al panel de
 *  zona (re-render). */
export const BOTON_OK_DETENCION = 'OK, me las piro';

/** Texto de la CASA RANDOM si la misión ya está completada. La escena
 *  `MonteScene` muestra este texto en lugar del botón "Inspeccionar…" cuando
 *  `GameState.misionCompletada('la-casa-random')` es true. */
export const CASA_RANDOM_COMPLETADA =
  'Ya le disteis un buen repaso a la casa random. Otro día será, máquina.';

// -------------------------------------------------------------------------
// Botones de acción visibles en el panel de cada zona
// -------------------------------------------------------------------------

/** Etiqueta del botón que arranca la Misión 1 desde la CASA RANDOM. */
export const BOTON_INSPECCIONAR_CASA_RANDOM = 'Inspeccionar la casa random';

/** Etiqueta del botón "Volver al barrio" — usado por las 3 escenas al
 *  pie del panel para volver al HUB. Se centraliza aquí para que las
 *  tres escenas usen exactamente el mismo string. */
export const BOTON_VOLVER_BARRIO = '← Volver al barrio';

/** Etiqueta del botón que navega LaCasaScene → InteriorCasaScene. */
export const BOTON_ENTRAR_DENTRO = 'Entrar dentro';

/** Etiqueta del botón que navega LaCasaScene → CampoFutbolScene. */
export const BOTON_BAJAR_CAMPO = 'Bajar al campo de fútbol';

/** Etiqueta del botón que navega CampoFutbolScene → LaCasaScene. */
export const BOTON_SUBIR_CASA = '← Subir a la casa';
